import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "April 2025";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-black">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-black/70 leading-relaxed text-base">
      {children}
    </div>
  </section>
);

const PrivacyPage = () => {
  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      <section className="relative pt-28 md:pt-36 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <h1 className="mt-6 text-4xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-white/55 text-sm">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="bg-white text-black py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-black/70 leading-relaxed">
            Good Fruit App (“Good Fruit,” “we,” “us,” “our”) is operated by
            Fathertime Technologies. We treat your information the way the
            Bible treats stewardship — with care, accountability, and
            transparency. This policy explains what we collect, why, and the
            choices you have.
          </p>

          <Section title="1. Information we collect">
            <p>
              <strong>Account & contact information.</strong> When you sign up,
              join the waitlist, enroll in the course, or contact us, we
              collect your name, email, and any details you choose to share.
            </p>
            <p>
              <strong>Payment information.</strong> Subscriptions are processed
              by <strong>Stripe</strong>. We never see or store your full card
              number. Stripe sends us only the minimum information needed to
              manage your subscription (e.g., subscription status, billing
              email).
            </p>
            <p>
              <strong>Chat conversations with Melanie.</strong> Conversations
              with our AI assistant are processed by Anthropic (Claude) and
              stored in our database so the assistant can remember context and
              we can improve quality. Do not share sensitive financial,
              medical, or legal details in chat.
            </p>
            <p>
              <strong>Funding intake forms.</strong> If you apply for funding,
              the information you submit is used only to prequalify your
              request and is shared with our partner LMI Marketing & Business
              Solutions Group, Inc.
            </p>
            <p>
              <strong>Usage data & cookies.</strong> We use standard analytics
              and local storage to understand how the app is used and to
              remember your preferences (e.g., your chat session). You can
              clear local storage in your browser at any time.
            </p>
          </Section>

          <Section title="2. How we use your information">
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and improve the Good Fruit platform</li>
              <li>To process your subscription, send receipts, and notify you of plan changes</li>
              <li>To respond to your support questions and feedback</li>
              <li>To send occasional product updates or ministry resources you opt into</li>
              <li>To comply with legal obligations</li>
            </ul>
            <p>
              We do <strong>not</strong> sell your personal information.
            </p>
          </Section>

          <Section title="3. Service providers we use">
            <p>
              We rely on the following trusted third parties; their privacy
              policies apply when you use their services through Good Fruit:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Stripe</strong> — payment processing and subscription
                management
              </li>
              <li>
                <strong>Anthropic</strong> — powers the Melanie AI assistant
              </li>
              <li>
                <strong>MongoDB Atlas</strong> — secure data storage
              </li>
              <li>
                <strong>IdentityIQ</strong> & <strong>CreditScoreIQ</strong>
                {" "}— affiliate partners for credit monitoring and DIY repair
                (you only share data with them if you sign up directly through
                their links)
              </li>
              <li>
                <strong>LMI Marketing & Business Solutions Group, Inc.</strong>
                {" "}— partner for funding prequalification
              </li>
            </ul>
          </Section>

          <Section title="4. Affiliate disclosure">
            <p>
              Some links on Good Fruit (notably IdentityIQ and CreditScoreIQ)
              are partner referral links. We may earn a small commission if
              you sign up through them — at no additional cost to you. We
              only partner with services we believe genuinely help our
              members.
            </p>
          </Section>

          <Section title="5. Your choices">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Access, correct, or delete</strong> your information by
                emailing us at the address below.
              </li>
              <li>
                <strong>Cancel your subscription</strong> anytime through the
                link in any Stripe receipt email.
              </li>
              <li>
                <strong>Unsubscribe</strong> from product or marketing emails
                via the unsubscribe link in any message.
              </li>
              <li>
                <strong>Clear chat history</strong> by clicking “New” in the
                Melanie chat panel.
              </li>
            </ul>
          </Section>

          <Section title="6. Children">
            <p>
              Good Fruit is not directed to children under 13, and we do not
              knowingly collect information from them. If you believe a child
              has provided us information, please contact us so we can remove
              it.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We use industry-standard safeguards to protect your information,
              but no system is 100% secure. Please do not share passwords, tax
              IDs, or sensitive financial details in unsolicited messages or in
              the AI chat.
            </p>
          </Section>

          <Section title="8. Changes to this policy">
            <p>
              We may update this policy as Good Fruit grows. Material changes
              will be communicated by email or in-app notice.
            </p>
          </Section>

          <Section title="9. Contact">
            <p>
              Questions, requests, or concerns? Email us — we read every
              message personally.
            </p>
            <p>
              <strong>Good Fruit App / Fathertime Technologies</strong>
              <br />
              Founder: Min. Tyree A. Huddleston III
            </p>
          </Section>

          <div className="mt-12 rounded-xl bg-[#f6faf9] border border-black/10 p-5 text-sm text-black/55 leading-relaxed">
            <strong className="text-black">Note:</strong> This Privacy Policy
            is provided as a starting template for a Christian-based startup.
            Before commercial launch, please have it reviewed by a qualified
            attorney to ensure it meets your jurisdiction&apos;s requirements
            (GDPR, CCPA, etc.).
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default PrivacyPage;
